import type {
  AccessControl,
  ApplyTemplateRequest,
  Channel,
  ChannelResponse,
  ChannelsResponse,
  CreateChannelRequest,
  CreateTemplateRequest,
  TemplateResponse,
  UpdateChannelConfigRequest,
  ViewRestrictions,
  ViewType,
} from "@/models/channel.model"
import type { HttpClient } from "@/services/http-common"
import ApiServiceFactory from "@/services/api-service-factory"

export class ChannelService {
  private httpClient: HttpClient

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient
  }

  async getChannels(): Promise<Channel[]> {
    const response = await this.httpClient.get<ChannelsResponse>("/channels")
    return response.channels || []
  }

  async getChannelsByUser(): Promise<Channel[]> {
    const response = await this.httpClient.get<Channel[]>(`/channels`)
    return response || []
  }

  async getChannelsByLocation(locationId: string): Promise<Channel[]> {
    const response = await this.httpClient.get<Channel[]>(`/channels`, {
      params: { locationId },
    })
    return response || []
  }

  async getChannelsByLocationAndDiscipline(locationId: string, discipline: string): Promise<Channel[]> {
    const response = await this.httpClient.get<Channel[]>(`/channels`, {
      params: { locationId, discipline },
    })
    return response || []
  }

  async getGeneralChannels(discipline?: string): Promise<Channel[]> {
    const params: Record<string, string> = { general: "true" }
    if (discipline)
      params.discipline = discipline
    const response = await this.httpClient.get<Channel[]>(`/channels`, { params })
    return response || []
  }

  async getFilteredChannels(filters: {
    locationId?: string
    discipline?: string
    general?: boolean
  }): Promise<Channel[]> {
    const params: Record<string, string> = {}

    if (filters.locationId)
      params.locationId = filters.locationId
    if (filters.discipline)
      params.discipline = filters.discipline
    if (filters.general)
      params.general = "true"

    const response = await this.httpClient.get<Channel[]>(`/channels`, { params })
    return response || []
  }

  async getChannelsForUnitSync(unitCode: string, discipline?: string): Promise<Channel[]> {
    const params: Record<string, string> = {}

    if (discipline)
      params.discipline = discipline

    const response = await this.httpClient.get<Channel[]>(`/channels/unit-sync/${encodeURIComponent(unitCode)}`, { params })
    return response || []
  }

  async checkChannelCodeAvailability(code: string): Promise<{ available: boolean, code: string }> {
    const response = await this.httpClient.get<{ available: boolean, code: string }>(`/channels/check-availability/${encodeURIComponent(code)}`)
    return response
  }

  async createChannel(channelData: CreateChannelRequest): Promise<ChannelResponse> {
    const response = await this.httpClient.post<ChannelResponse>("/channels", channelData)
    return response
  }

  async updateChannelConfig(
    channelId: string,
    config: UpdateChannelConfigRequest,
  ): Promise<ChannelResponse> {
    const response = await this.httpClient.post<ChannelResponse>(
      `/channels/${channelId}/config`,
      config,
    )
    console.log('respuesta el servidor: ', channelId)
    console.log('respuesta el servidor: ', config)
    console.log('respuesta el servidor: ', response)

    if (Array.isArray(response) && response.length === 0) {
      throw new Error("ERROR");
    }
    return response
  }

  async updateMultipleChannelConfigs(
    channelIds: string[],
    config: UpdateChannelConfigRequest,
  ): Promise<ChannelResponse[]> {
    const response = await this.httpClient.patch<ChannelResponse[]>(
      `/channels/config`,
      {
        channelIds,
        configDto: config,
      },
    )
    return response
  }

  async updateChannelViewType(
    channelId: string,
    viewType: ViewType,
  ): Promise<ChannelResponse> {
    const response = await this.httpClient.patch<ChannelResponse>(
      `/channels/${channelId}/view-type`,
      { viewType },
    )
    return response
  }

  async updateChannelUnit(
    channelId: string,
    unit: string,
  ): Promise<ChannelResponse> {
    const response = await this.httpClient.patch<ChannelResponse>(
      `/channels/${channelId}/unit`,
      { unit },
    )
    return response
  }

  async deleteChannel(channelId: string): Promise<ChannelResponse> {
    const response = await this.httpClient.delete<ChannelResponse>(`/channels/${channelId}`)
    if (Array.isArray(response) && response.length === 0) {
      throw new Error("ERROR");
    }
    return response
  }

  async toggleChannelStatus(channelId: string, status: boolean): Promise<ChannelResponse> {
    const response = await this.httpClient.post<ChannelResponse>(
      `/channels/${channelId}/config`,
      { isActive: status },
    )
    return response
  }

  async getChannelConfig(channelId: string): Promise<ChannelResponse> {
    const response = await this.httpClient.get<ChannelResponse>(`/channels/${channelId}/config`)
    return response
  }

  async updateChannelRestrictions(
    channelId: string,
    viewRestrictions: ViewRestrictions,
    accessControl: AccessControl,
  ): Promise<ChannelResponse> {
    const response = await this.httpClient.post<ChannelResponse>(
      `/channels/${channelId}/restrictions`,
      {
        viewRestrictions,
        accessControl,
      },
    )
    return response
  }

  async updateChannelParams(
    params: Record<string, unknown>,
    channelIds: string[],
  ): Promise<{
    success: boolean
    message: string
    updatedChannels: string[]
    errors?: string[]
  }> {
    const response = await this.httpClient.patch<{
      success: boolean
      message: string
      updatedChannels: string[]
      errors?: string[]
    }>("/channels/params", {
      params,
      channelIds,
    })
    return response
  }

  async getChannelsByTemplate(template: boolean, userId?: string): Promise<Channel[]> {
    const params: Record<string, string> = { template: template.toString() }
    if (userId) {
      params.userId = userId
    }

    const response = await this.httpClient.get<Channel[]>("/channels/by-template", { params })
    return response || []
  }

  async getChannelsByLocationType(locationType: string, discipline?: string): Promise<Channel[]> {
    const params: Record<string, string> = {}
    if (discipline) {
      params.discipline = discipline
    }
    const response = await this.httpClient.get<Channel[]>(`/channels/by-location-type/${locationType}`, { params })
    return response || []
  }

  async createTemplateChannel(request: CreateTemplateRequest): Promise<TemplateResponse> {
    const response = await this.httpClient.post<TemplateResponse>(
      `/channels/${request.originalChannelId}/create-template`,
      { userId: request.userId },
    )
    if (Array.isArray(response) && response.length === 0) {
      throw new Error("ERROR");
    }
    return response
  }

  async applyTemplateToMainChannel(request: ApplyTemplateRequest): Promise<TemplateResponse> {
    const response = await this.httpClient.post<TemplateResponse>(
      "/channels/apply-template",
      {
        templateChannelId: request.templateChannelId,
        mainChannelId: request.mainChannelId,
      },
    )
    if (Array.isArray(response) && response.length === 0) {
      throw new Error("ERROR");
    }
    return response
  }
}

export const channelService = ApiServiceFactory.createService(ChannelService, "main")
export default ChannelService
